import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { ordersService } from '../../../../../services/orders/orders.service';
import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import type { PaymentlineView } from '../../../../../services/payments/payments.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';
import type { OrderlineSelection } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import type { DiscountMode } from '../../receipt/actions/global-discount-modal/GlobalDiscountModal';
import type { ApprovalActionType, ApprovalPayload } from '../section/useApprovals';
import {
  buildCompedItemDiscountPayload,
  buildItemDiscountPayload,
  getTotalDiscountDelta,
} from '../shared/discount.utils';
import {
  buildCancelApprovalPayload,
  hasPaidPaymentline,
  resolveCancelScope,
} from '../../../core/order-cancel-flow';

export type OrderItemActionsStep = 'idle' | 'itemSelect' | 'itemDiscount' | 'oth' | 'cancel';

export type OrderItemActionsStepKey = 'itemDiscount' | 'oth' | 'cancel';

export type UseOrderItemActionsFlowParams = {
  tenantId: string;
  employeeSession: string | null;
  orderId: number | null;
  isManager: boolean;
  isTakeAway: boolean;
  orderlines: OrderlineView[];
  paymentlines: PaymentlineView[];
  remainingAfterGlobalDiscount: number;
  requestApproval: (actionType: ApprovalActionType, payload: ApprovalPayload) => void;
  // Hosted mode only: dismissing a step tears the whole flow down instead of returning to the order.
  onDismiss?: () => void;
};

export type OrderItemActionsFlowController = {
  step: OrderItemActionsStep;
  isSaving: boolean;
  isManager: boolean;
  isTakeAway: boolean;
  orderlines: OrderlineView[];
  selections: OrderlineSelection[];
  selectionSubtotal: number;
  openStep: (key: OrderItemActionsStepKey) => void;
  openItemDiscountForSelections: (selections: OrderlineSelection[], subtotal: number) => void;
  dismiss: () => void;
  confirmItemSelect: (selections: OrderlineSelection[], subtotal: number) => void;
  confirmOth: (selections: OrderlineSelection[]) => void;
  confirmCancel: (selections: OrderlineSelection[]) => void;
  backToItemSelect: () => void;
  removeItemDiscount: () => Promise<void>;
  confirmItemDiscount: (discountValue: number, mode: DiscountMode) => void;
};

// Summary: Single item-actions step machine shared by the hosted flow and the open order.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
export function useOrderItemActionsFlow({
  tenantId,
  employeeSession,
  orderId,
  isManager,
  isTakeAway,
  orderlines,
  paymentlines,
  remainingAfterGlobalDiscount,
  requestApproval,
  onDismiss,
}: UseOrderItemActionsFlowParams): OrderItemActionsFlowController {
  const [step, setStep] = useState<OrderItemActionsStep>('idle');
  const [selections, setSelections] = useState<OrderlineSelection[]>([]);
  const [selectionSubtotal, setSelectionSubtotal] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const inFlightRef = useRef(false);

  const dismiss = useCallback(() => {
    setStep('idle');
    setSelections([]);
    onDismiss?.();
  }, [onDismiss]);

  const openStep = useCallback((key: OrderItemActionsStepKey) => {
    setStep(key === 'itemDiscount' ? 'itemSelect' : key);
  }, []);

  const openItemDiscountForSelections = useCallback((nextSelections: OrderlineSelection[], subtotal: number) => {
    setSelections(nextSelections);
    setSelectionSubtotal(subtotal);
    setStep('itemDiscount');
  }, []);

  const confirmItemSelect = useCallback((nextSelections: OrderlineSelection[], subtotal: number) => {
    setSelections(nextSelections);
    setSelectionSubtotal(subtotal);
    setStep('itemDiscount');
  }, []);

  const confirmOth = useCallback((nextSelections: OrderlineSelection[]) => {
    if (!orderId || !employeeSession) return;
    const items = buildCompedItemDiscountPayload(nextSelections, orderlines);
    // Compare the net comped delta (gross − existing) against the remaining balance.
    const totalCompedDelta = getTotalDiscountDelta(items);
    if (totalCompedDelta > remainingAfterGlobalDiscount) {
      notifications.show({ title: 'הנחה חורגת מהיתרה לתשלום', message: 'ההנחה הנבחרת גבוהה מהסכום שנותר לתשלום', color: 'red', autoClose: 3000 });
      return;
    }
    // OTH on an already-comped line changes nothing — no second approval.
    if (totalCompedDelta <= 0) {
      notifications.show({ title: 'הפריט כבר על חשבון הבית', message: 'לא בוצעה פעולה נוספת', color: 'blue', autoClose: 3000 });
      dismiss();
      return;
    }
    setStep('idle');
    requestApproval('on_the_house', {
      actionType: 'on_the_house',
      items: items.map(({ orderlineId, quantity, discountInput }) => ({ orderlineId, quantity, discountInput })),
    });
  }, [dismiss, employeeSession, orderId, orderlines, remainingAfterGlobalDiscount, requestApproval]);

  const confirmCancel = useCallback((nextSelections: OrderlineSelection[]) => {
    const cancelScope = resolveCancelScope(orderlines, nextSelections);
    if (cancelScope === 'order' && hasPaidPaymentline(paymentlines)) {
      notifications.show({ color: 'red', title: 'לא ניתן לבטל הזמנה', message: 'קיימת שורת תשלום משולמת', autoClose: 3000 });
      dismiss();
      return;
    }
    setStep('idle');
    requestApproval('cancel', buildCancelApprovalPayload(nextSelections, cancelScope));
  }, [dismiss, orderlines, paymentlines, requestApproval]);

  const backToItemSelect = useCallback(() => setStep('itemSelect'), []);

  const removeItemDiscount = useCallback(async () => {
    if (!orderId || !employeeSession || !isManager || inFlightRef.current) return;
    inFlightRef.current = true;
    setIsSaving(true);
    try {
      await ordersService.applyItemDiscount(
        tenantId,
        orderId,
        selections.map((selection) => ({ orderlineId: selection.orderlineId, quantity: selection.quantity, discountInput: null })),
        employeeSession,
      );
      dismiss();
    } catch (err) {
      notifications.show({ title: 'שגיאה בהסרת הנחת פריטים', message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''), color: 'red', autoClose: 3000 });
    } finally {
      setIsSaving(false);
      inFlightRef.current = false;
    }
  }, [dismiss, employeeSession, isManager, orderId, selections, tenantId]);

  const confirmItemDiscount = useCallback((discountValue: number, mode: DiscountMode) => {
    if (!orderId || !employeeSession) return;
    const items = buildItemDiscountPayload(selections, orderlines, discountValue, mode);
    if (getTotalDiscountDelta(items) > remainingAfterGlobalDiscount) {
      notifications.show({ title: 'הנחה חורגת מהיתרה לתשלום', message: 'ההנחה הנבחרת גבוהה מהסכום שנותר לתשלום', color: 'red', autoClose: 3000 });
      return;
    }
    setStep('idle');
    requestApproval('item_discount', {
      actionType: 'item_discount',
      items: items.map(({ orderlineId, quantity, discountInput }) => ({ orderlineId, quantity, discountInput: discountInput ?? 0 })),
    });
    setSelections([]);
  }, [employeeSession, orderId, orderlines, remainingAfterGlobalDiscount, requestApproval, selections]);

  return {
    step,
    isSaving,
    isManager,
    isTakeAway,
    orderlines,
    selections,
    selectionSubtotal,
    openStep,
    openItemDiscountForSelections,
    dismiss,
    confirmItemSelect,
    confirmOth,
    confirmCancel,
    backToItemSelect,
    removeItemDiscount,
    confirmItemDiscount,
  };
}
